/**
 * @file complaintSlice.js
 * @description Redux slice for managing complaints data.
 * This file contains the actions and reducers related to complaints, including setting
 * the complaint list, route, and selected complaint. It also handles resetting the state.
 * 
 * @author Govind Dindewad
 */

import { createSlice } from '@reduxjs/toolkit';

// Initial state for the complaint slice
const initialState = {
    complaintList: null,          // Holds the list of complaints
    complaintRoute: '/complaints', // Default route for complaints
    selectedComplaint: null,      // Holds the selected complaint details
    poolDetails:null
};

// Creating the complaint slice using Redux Toolkit
const complaintSlice = createSlice({
    name: 'complaints',         // Name of the slice in Redux store
    initialState,               // Initial state for the slice
    reducers: {
        /**
         * Set the complaint list.
         * @param {Object} state - The current state.
         * @param {Object} action - The action containing the new complaint list.
         */
        setComplaintList: (state, action) => {
            state.complaintList = action.payload; // Update complaint list in the state
        },

        /**
         * Set the complaint route.
         * @param {Object} state - The current state.
         * @param {Object} action - The action containing the new route.
         */
        setComplaintRoute: (state, action) => {
            state.complaintRoute = action.payload; // Update complaint route path
        },

        /**
         * Set the selected complaint.
         * @param {Object} state - The current state.
         * @param {Object} action - The action containing the selected complaint.
         */
        setSelectedComplaint: (state, action) => {
            state.selectedComplaint = action.payload; // Update selected complaint in the state
        },
        
        /**
         * Set the pool details from complaint
         * @param {Object} state - The current state.
         * @param {Object} action - The action containing the selected complaint.
         */
        setPoolDetails: (state, action) => {
            state.poolDetails = action.payload; // Update selected complaint in the state
        },

    },

    /**
     * Extra reducers handle external actions (like resetting the state).
     * Here, we listen for the 'RESET_STATE' action and reset the state to its initial values.
     */
    extraReducers: (builder) => {
        builder.addCase('RESET_STATE', () => initialState); // Reset state when the 'RESET_STATE' action is dispatched
    }
});

// Exporting the actions generated by createSlice for use in dispatching
export const { setComplaintList, setComplaintRoute, setSelectedComplaint, setPoolDetails } = complaintSlice.actions;

// Exporting the reducer to be used in the store configuration
export default complaintSlice.reducer;
